import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _typeof from "@babel/runtime/helpers/typeof";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
var _excluded = ["size", "className", "multiple", "bordered", "dropdownClassName", "expandIcon", "showSearch", "allowClear", "placeholder", "notFoundContent", "getPopupContainer", "onDropdownVisibleChange"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React, { useCallback, useState } from 'react';
import RcCascader from 'rc-cascader';
import omit from 'rc-util/lib/omit';
import devWarning from '../_utils/devWarning';
import { classnames } from '../../utils';
import { usePrefixCls } from '../../config/hooks/usePrefixCls';
import { CloseOne, ArrowRightOne, LoadingStatic, Search, Close, ArrowDownOne, ArrowUpOne, CheckBold, RectRound } from '@tencent/adp-icons-react';
import { getZIndex } from '@tencent/glue-dom-helper';
function highlightKeyword(str, lowerKeyword, prefixCls) {
  var cells = str.toLowerCase().split(lowerKeyword).reduce(function (list, cur, index) {
    return index === 0 ? [cur] : [].concat(_toConsumableArray(list), [lowerKeyword, cur]);
  }, []);
  var fillCells = [];
  var start = 0;
  cells.forEach(function (cell, index) {
    var end = start + cell.length;
    var originWorld = str.slice(start, end);
    start = end;
    if (index % 2 === 1) {
      originWorld = React.createElement("span", {
        className: "".concat(prefixCls, "-menu-item-keyword"),
        key: "seperator-".concat(index)
      }, originWorld);
    }
    fillCells.push(originWorld);
  });
  return fillCells;
}
var defaultSearchRender = function defaultSearchRender(inputValue, path, prefixCls, fieldNames) {
  var optionList = [];
  var lower = inputValue.toLowerCase();
  path.forEach(function (node, index) {
    if (index !== 0) {
      optionList.push(' / ');
    }
    var label = node[fieldNames.label];
    var type = _typeof(label);
    if (type === 'string' || type === 'number') {
      label = highlightKeyword(String(label), lower, prefixCls);
    }
    optionList.push(label);
  });
  return optionList;
};
var Cascader = React.forwardRef(function (props, ref) {
  var _classnames;
  var customizeSize = props.size,
    className = props.className,
    multiple = props.multiple,
    _props$bordered = props.bordered,
    bordered = _props$bordered === void 0 ? true : _props$bordered,
    dropdownClassName = props.dropdownClassName,
    expandIcon = props.expandIcon,
    showSearch = props.showSearch,
    _props$allowClear = props.allowClear,
    allowClear = _props$allowClear === void 0 ? false : _props$allowClear,
    _props$placeholder = props.placeholder,
    placeholder = _props$placeholder === void 0 ? '请选择' : _props$placeholder,
    notFoundContent = props.notFoundContent,
    getPopupContainer = props.getPopupContainer,
    onDropdownVisibleChange = props.onDropdownVisibleChange,
    rest = _objectWithoutProperties(props, _excluded);
  var restProps = omit(rest, ['suffixIcon']);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  var _useState3 = useState(1040),
    _useState4 = _slicedToArray(_useState3, 2),
    cascaderZIndex = _useState4[0],
    setCascaderZIndex = _useState4[1];
  if (process.env.NODE_ENV !== 'production') {
    devWarning(!multiple || !props.displayRender, 'Cascader', '`displayRender` not work on `multiple`. Please use `tagRender` instead.');
  }
  var mergedNotFoundContent = notFoundContent || 'empty';
  var prefixCls = usePrefixCls('cascader');
  var dropdownCls = usePrefixCls('cascader-panel');
  var mergedDropdownClassName = classnames(dropdownClassName, "".concat(prefixCls, "-dropdown"), "".concat(dropdownCls, "-").concat(cascaderZIndex));
  var mergedSize = customizeSize || 'md';
  var loadingIcon = React.createElement("span", {
    className: "".concat(dropdownCls, "-menu-item-loading-icon")
  }, React.createElement(LoadingStatic, {
    spin: true
  }));
  var checkable = React.useMemo(function () {
    return multiple ? React.createElement("span", {
      className: "".concat(dropdownCls, "-checkbox-inner ").concat(dropdownCls, "-checkbox-square")
    }, React.createElement(CheckBold, {
      size: 10
    }), React.createElement(RectRound, {
      size: 6
    })) : false;
  }, [multiple]);
  var mergedShowSearch = React.useMemo(function () {
    if (!showSearch) {
      return showSearch;
    }
    var searchConfig = {
      render: defaultSearchRender
    };
    if (_typeof(showSearch) === 'object') {
      searchConfig = _objectSpread(_objectSpread({}, searchConfig), showSearch);
    }
    return searchConfig;
  }, [showSearch]);
  var dropdownVisibleChange = useCallback(function (val) {
    onDropdownVisibleChange === null || onDropdownVisibleChange === void 0 ? void 0 : onDropdownVisibleChange(val);
    setOpen(val);
    setCascaderZIndex(1040 + getZIndex());
  }, [onDropdownVisibleChange]);
  var expandArrowNode = open ? React.createElement(ArrowUpOne, {
    size: 14
  }) : React.createElement(ArrowDownOne, {
    size: 14
  });
  return React.createElement(React.Fragment, null, React.createElement("style", null, ".".concat(dropdownCls, "-").concat(cascaderZIndex, " { z-index: ").concat(cascaderZIndex, "}")), React.createElement(RcCascader, _objectSpread(_objectSpread({
    prefixCls: prefixCls,
    className: classnames(dropdownCls, (_classnames = {}, _defineProperty(_classnames, "".concat(prefixCls, "-lg"), mergedSize === 'lg'), _defineProperty(_classnames, "".concat(prefixCls, "-sm"), mergedSize === 'sm'), _defineProperty(_classnames, "".concat(prefixCls, "-borderless"), !bordered), _classnames), className)
  }, restProps), {}, {
    notFoundContent: mergedNotFoundContent,
    allowClear: allowClear,
    expandIcon: React.createElement(ArrowRightOne, {
      size: 14
    }),
    showSearch: mergedShowSearch,
    inputIcon: mergedShowSearch ? React.createElement(Search, null) : expandArrowNode,
    removeIcon: React.createElement(CloseOne, null),
    clearIcon: React.createElement(Close, null),
    placeholder: placeholder,
    onDropdownVisibleChange: dropdownVisibleChange,
    loadingIcon: loadingIcon,
    checkable: checkable,
    dropdownClassName: mergedDropdownClassName,
    dropdownPrefixCls: dropdownCls,
    getPopupContainer: getPopupContainer,
    ref: ref
  })));
});
Cascader.displayName = 'Cascader';
export { Cascader };